// template-renderer.js — Unified template rendering module.
// All rendering paths (editor, dashboard, marketplace, PDF) go through this module.

// =========================================================================
// CSS Cache
// =========================================================================

var cssCache = new Map(); // albumId -> { rawCSS, globalImports: Map<hash, {count, el}> }

function clearCSSCache() {
  cssCache.forEach(function(entry) {
    entry.globalImports.forEach(function(info) {
      if (info.el && info.el.parentNode) info.el.remove();
    });
    entry.globalImports.clear();
  });
  cssCache.clear();
}

// =========================================================================
// Helpers
// =========================================================================

function simpleHash(str) {
  var hash = 0;
  for (var i = 0; i < str.length; i++) {
    var char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return (hash >>> 0).toString(36);
}

// Map a seed string to a local photo from the 250-image pool using deterministic hash
function placeholderUrl(seed, isSpread) {
  seed = seed || 'default';
  var h = 0;
  for (var i = 0; i < seed.length; i++) { h = ((h << 5) - h) + seed.charCodeAt(i); h |= 0; }
  if (isSpread) {
    var idx = (Math.abs(h) % 100) + 1;
    var padded = idx < 10 ? '00' + idx : idx < 100 ? '0' + idx : String(idx);
    return 'images/placeholders-spread/spread-' + padded + '.jpg';
  }
  var idx = (Math.abs(h) % 250) + 1;
  var padded = idx < 10 ? '00' + idx : idx < 100 ? '0' + idx : String(idx);
  return 'images/placeholders/photo-' + padded + '.jpg';
}

function normalizePageSize(size) {
  if (typeof size === 'number') return { width: size, height: size };
  if (size && typeof size.width === 'number' && typeof size.height === 'number') {
    return { width: size.width, height: size.height };
  }
  return { width: 400, height: 400 };
}

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function waitForImages(root) {
  var images = root.querySelectorAll('img');
  return Promise.all(Array.from(images).map(function(img) {
    if (img.complete) return Promise.resolve();
    return new Promise(function(resolve) {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
  }));
}

// =========================================================================
// processCSS — CSS scope injection
// =========================================================================

// Parse a single CSS rule's selectors and prefix them with scopedClass.
// renderClass selectors get same-element treatment: .mini-page -> .scope.mini-page
function _scopeSelectors(selectorText, scopedClass, renderClass) {
  var parts = selectorText.split(',');
  var out = [];
  for (var i = 0; i < parts.length; i++) {
    var sel = parts[i].trim();
    if (!sel) continue;

    if (sel === ':root') {
      out.push('.' + scopedClass);
    } else if (renderClass && sel.indexOf('.' + renderClass) === 0) {
      // Selector starts with the renderClass — same-element prefix
      var rest = sel.substring(('.' + renderClass).length);
      // Check that the next char is a selector boundary (space, :, ., #, [, or end)
      if (rest.length === 0 || /^[\s:.#\[]/.test(rest)) {
        out.push('.' + scopedClass + '.' + renderClass + rest);
      } else {
        // Not an exact match (e.g. .mini-page-other), treat as generic
        out.push('.' + scopedClass + ' ' + sel);
      }
    } else {
      out.push('.' + scopedClass + ' ' + sel);
    }
  }
  return out.join(', ');
}

// Replace animation-name references to match renamed @keyframes
function _replaceAnimationRefs(cssText, keyframeMap) {
  var result = cssText;
  var names = Object.keys(keyframeMap);
  for (var i = 0; i < names.length; i++) {
    var orig = names[i];
    var renamed = keyframeMap[orig];
    if (orig === renamed) continue;
    // Replace animation: ... orig ... ;
    var re = new RegExp('(animation\\s*:[^;}]*)' + escapeRegex(orig) + '([\\s,;})]|$)', 'g');
    result = result.replace(re, '$1' + renamed + '$2');
    // Replace animation-name: orig
    var re2 = new RegExp('(animation-name\\s*:\\s*)' + escapeRegex(orig) + '([\\s,;})]|$)', 'g');
    result = result.replace(re2, '$1' + renamed + '$2');
  }
  return result;
}

function _extractLeadingImports(cssText) {
  var imports = [];
  var rest = cssText;

  while (true) {
    var start = 0;
    var moved = true;
    while (moved) {
      moved = false;
      var leadingWhitespace = rest.slice(start).match(/^\s*/)[0];
      if (leadingWhitespace) {
        start += leadingWhitespace.length;
        moved = true;
      }
      if (rest.slice(start, start + 2) === '/*') {
        var commentEnd = rest.indexOf('*/', start + 2);
        if (commentEnd === -1) break;
        start = commentEnd + 2;
        moved = true;
      }
    }
    if (rest.slice(start, start + 7).toLowerCase() !== '@import') break;

    var quote = null;
    var parenDepth = 0;
    var end = -1;
    for (var i = start; i < rest.length; i++) {
      var ch = rest[i];
      if (quote) {
        if (ch === quote && rest[i - 1] !== '\\') quote = null;
      } else if (ch === '"' || ch === "'") {
        quote = ch;
      } else if (ch === '(') {
        parenDepth++;
      } else if (ch === ')' && parenDepth > 0) {
        parenDepth--;
      } else if (ch === ';' && parenDepth === 0) {
        end = i + 1;
        break;
      }
    }
    if (end === -1) break;

    imports.push(rest.slice(start, end).trim());
    rest = rest.slice(end);
  }

  return { imports: imports, css: rest };
}

function _stripLeadingCSSComments(cssText) {
  var out = cssText.trim();
  while (out.slice(0, 2) === '/*') {
    var end = out.indexOf('*/');
    if (end === -1) break;
    out = out.slice(end + 2).trim();
  }
  return out;
}

function processCSS(rawCSS, scopedClass, renderClass) {
  if (!rawCSS) return { globalCSS: '', scopedCSS: '' };

  var globalParts = [];
  var scopedParts = [];
  var keyframeMap = {}; // original name -> scoped name

  var extracted = _extractLeadingImports(rawCSS);
  if (extracted.imports.length) {
    // Normalise relative font URLs: the template CSS loaded from
    // /make_album/templates/xxx/style.css resolves ../fonts/ correctly,
    // but when @import rules are injected into a <style> tag they resolve
    // against the document URL (/make_album/editor.html), losing one level.
    // Rewrite ../fonts/ → /make_album/fonts/ so browsers find the files.
    extracted.imports = extracted.imports.map(function(imp) {
      return imp.replace(
        /url\(['"]?\.\.\/(fonts\/[^'")\s]+)['"]?\)/g,
        'url("/make_album/$1")'
      );
    });
    globalParts = globalParts.concat(extracted.imports);
    rawCSS = extracted.css;
  }

  // Simplest robust approach: split on } that ends a top-level rule.
  // We track brace depth to handle @media / @keyframes blocks.
  var depth = 0;
  var start = 0;
  var i = 0;

  while (i < rawCSS.length) {
    var ch = rawCSS[i];
    if (ch === '{') {
      depth++;
    } else if (ch === '}') {
      depth--;
      if (depth === 0) {
        // End of a top-level rule (or @-rule block)
        var block = rawCSS.substring(start, i + 1).trim();
        start = i + 1;

        if (!block) continue;

        var typedBlock = _stripLeadingCSSComments(block);

        // Check if this is an @-rule
        if (typedBlock.charAt(0) === '@') {
          var atRuleMatch = typedBlock.match(/^@(import|font-face|keyframes|media)\b/);
          if (atRuleMatch) {
            var atType = atRuleMatch[1];
            if (atType === 'import' || atType === 'font-face') {
              globalParts.push(typedBlock);
            } else if (atType === 'keyframes') {
              // Rename keyframes with scope suffix
              var kfMatch = typedBlock.match(/@keyframes\s+([^{\s]+)\s*\{/);
              if (kfMatch) {
                var origName = kfMatch[1];
                var scopedName = origName + '-' + scopedClass.substring(scopedClass.length - 6);
                keyframeMap[origName] = scopedName;
                scopedParts.push(typedBlock.replace(origName, scopedName));
              } else {
                scopedParts.push(typedBlock);
              }
            } else if (atType === 'media') {
              // Process rules inside @media
              var mediaInner = _processRuleBlock(typedBlock, scopedClass, renderClass, keyframeMap);
              scopedParts.push(mediaInner);
            }
          } else {
            // Unknown @-rule, keep as scoped
            scopedParts.push(typedBlock);
          }
        } else {
          // Regular style rule — scope the selectors
          var ruleMatch = typedBlock.match(/^([^{]+)\{(.*)\}$/s);
          if (ruleMatch) {
            var selectors = ruleMatch[1].trim();
            var body = ruleMatch[2].trim();
            var scopedSelectors = _scopeSelectors(selectors, scopedClass, renderClass);
            scopedParts.push(scopedSelectors + ' {' + body + '}');
          } else {
            scopedParts.push(block);
          }
        }
      }
    } else if (ch === '{' && depth === 0) {
      // First opening brace of a top-level rule — position saved
    }
    i++;
  }

  // Handle any trailing content
  if (start < rawCSS.length) {
    var trailing = rawCSS.substring(start).trim();
    if (trailing) {
      if (/^@import\b/i.test(trailing) || /^@font-face\b/i.test(trailing)) {
        globalParts.push(trailing);
      } else {
        scopedParts.push(trailing);
      }
    }
  }

  var scopedCSS = scopedParts.join('\n');
  // Replace animation references for renamed keyframes
  if (Object.keys(keyframeMap).length > 0) {
    scopedCSS = _replaceAnimationRefs(scopedCSS, keyframeMap);
  }

  return {
    globalCSS: globalParts.join('\n'),
    scopedCSS: scopedCSS
  };
}

// Process rules inside an @media block
function _processRuleBlock(mediaBlock, scopedClass, renderClass, keyframeMap) {
  // Extract the media query preamble and the inner rules
  var preambleMatch = mediaBlock.match(/^(@media\s+[^{]+)\s*\{/);
  if (!preambleMatch) return mediaBlock;
  var preamble = preambleMatch[1];

  // Extract inner content (everything between the outer braces)
  var innerStart = preambleMatch[0].length;
  var innerEnd = mediaBlock.lastIndexOf('}');
  if (innerEnd <= innerStart) return mediaBlock;
  var innerContent = mediaBlock.substring(innerStart, innerEnd).trim();

  // Process each inner rule
  var depth = 0;
  var start = 0;
  var innerRules = [];
  for (var i = 0; i < innerContent.length; i++) {
    if (innerContent[i] === '{') depth++;
    else if (innerContent[i] === '}') {
      depth--;
      if (depth === 0) {
        var rule = innerContent.substring(start, i + 1).trim();
        start = i + 1;
        if (!rule) continue;

        var normalizedRule = _stripLeadingCSSComments(rule);
        var ruleMatch = normalizedRule.match(/^([^{]+)\{(.*)\}$/s);
        if (ruleMatch) {
          var selectors = ruleMatch[1].trim();
          var body = ruleMatch[2].trim();
          selectors = _scopeSelectors(selectors, scopedClass, renderClass);
          innerRules.push(selectors + ' {' + body + '}');
        } else {
          innerRules.push(rule);
        }
      }
    }
  }

  return preamble + ' {\n' + innerRules.join('\n') + '\n}';
}

// =========================================================================
// normalizeSavedPageHtml — prevent double wrapper
// =========================================================================

function normalizeSavedPageHtml(html, renderClass) {
  if (!html) return html;

  var div = document.createElement('div');
  div.innerHTML = html;

  div.querySelectorAll('style').forEach(function(el) { el.remove(); });

  // Remove old template-scope-* classes from any element
  div.querySelectorAll('[class]').forEach(function(el) {
    var classes = el.className.split(/\s+/).filter(function(c) {
      return !c.startsWith('template-scope-');
    });
    el.className = classes.join(' ');
  });

  // If the root has the renderClass, unwrap it (extract children)
  // The new renderPage will re-wrap with fresh scope.
  if (renderClass && div.children.length === 1) {
    var root = div.children[0];
    if (root.classList.contains(renderClass)) {
      // Extract children of the renderClass root
      var inner = '';
      for (var i = 0; i < root.children.length; i++) {
        inner += root.children[i].outerHTML;
      }
      return inner;
    }
  }

  return div.innerHTML;
}

// =========================================================================
// inlineComputedVisualStyles — for captureElementToCanvas
// =========================================================================

function inlineComputedVisualStyles(root) {
  var visualProps = {
    boxShadow: 'box-shadow',
    background: 'background',
    backgroundColor: 'background-color',
    color: 'color',
    borderColor: 'border-color',
    borderRadius: 'border-radius',
    textShadow: 'text-shadow',
    opacity: 'opacity',
    fontFamily: 'font-family',
    fontSize: 'font-size',
    fontWeight: 'font-weight',
    fontStyle: 'font-style',
    lineHeight: 'line-height',
    letterSpacing: 'letter-spacing',
    textAlign: 'text-align',
    display: 'display',
    position: 'position',
    transform: 'transform',
    filter: 'filter',
    objectFit: 'object-fit',
    width: 'width',
    height: 'height',
    top: 'top',
    left: 'left',
    right: 'right',
    bottom: 'bottom'
  };
  var elements = [root].concat(Array.from(root.querySelectorAll('*')));
  var savedStyles = [];
  elements.forEach(function(el) {
    savedStyles.push(el.getAttribute('style') || '');
    var cs = getComputedStyle(el);
    Object.keys(visualProps).forEach(function(prop) {
      try {
        var val = cs[prop];
        if (val && val !== 'none' && val !== 'transparent' && val !== 'rgba(0, 0, 0, 0)' && val !== 'normal' && val !== 'auto') {
          el.style.setProperty(visualProps[prop], val);
        }
      } catch(e) {}
    });
  });
  return function restoreStyles() {
    elements.forEach(function(el, i) {
      el.setAttribute('style', savedStyles[i]);
    });
  };
}

// =========================================================================
// captureElementToCanvas — unified screenshot
// =========================================================================

function captureElementToCanvas(element, options) {
  options = options || {};
  var scale = options.scale || 2;
  var pageSize = options.pageSize;

  // 1. Wait for all images
  var images = element.querySelectorAll('img');
  var imgWait = Promise.all(Array.from(images).map(function(img) {
    if (img.complete) return Promise.resolve();
    return new Promise(function(resolve) {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
  }));

  return imgWait.then(function() {
    // 2. Wait for fonts
    if (document.fonts && document.fonts.ready) {
      return document.fonts.ready;
    }
  }).then(function() {
    // 3. Clone to off-screen DOM
    var clone = element.cloneNode(true);
    clone.style.position = 'absolute';
    clone.style.left = '-9999px';
    clone.style.top = '0';
    document.body.appendChild(clone);

    var restoreStyles = function() {};
    // 4. Clean edit controls
    clone.querySelectorAll('.canvas-fill-btn, .rotate-handle').forEach(function(el) { el.remove(); });
    clone.querySelectorAll('.img-slot.selected').forEach(function(el) { el.classList.remove('selected'); });
    clone.querySelectorAll('[contenteditable]').forEach(function(el) { el.setAttribute('contenteditable', 'false'); });

    // 5. Inline computed visual styles
    restoreStyles = inlineComputedVisualStyles(clone);

    // 6. Fix object-fit images
    clone.querySelectorAll('img').forEach(function(img) {
      var cs = getComputedStyle(img);
      if (cs.objectFit === 'cover') {
        img.style.objectFit = 'cover';
        img.style.width = '100%';
        img.style.height = '100%';
      }
    });

    // 6b. Replace cross-origin image src with same-origin blob URLs before
    // capture.  The browser may have cached a non-CORS response for these
    // URLs from the page display; fetch with cache:reload forces a fresh
    // CORS-enabled request.  Blob URLs are same-origin so html2canvas can
    // draw them without tainting the canvas.
    var imgReplacements = [];
    clone.querySelectorAll('img').forEach(function(img) {
      var src = img.src;
      if (!src || /^data:/i.test(src) || /^blob:/i.test(src)) return;
      var p = fetch(src, { mode: 'cors', cache: 'reload' })
        .then(function(r) {
          if (!r.ok) return null;
          return r.blob();
        })
        .then(function(blob) {
          if (!blob) { img.src = src; return; }
          var blobUrl = URL.createObjectURL(blob);
          img.src = blobUrl;
          return new Promise(function(resolve) {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          });
        })
        .catch(function() {
          img.src = src;
        });
      imgReplacements.push(p);
    });

    // 7. Wait for fonts again
    var fontWait = document.fonts && document.fonts.ready
      ? document.fonts.ready
      : Promise.resolve();

    return fontWait.then(function() {
      // Wait for image fetch→blob conversions before handing off
      return Promise.all(imgReplacements);
    }).then(function() {
      // 8. Capture
      var canvasOpts = {
        scale: scale,
        useCORS: true,
        allowTaint: false,
        backgroundColor: null
      };
      if (pageSize) {
        canvasOpts.width = pageSize.width;
        canvasOpts.height = pageSize.height;
      } else {
        canvasOpts.width = clone.clientWidth;
        canvasOpts.height = clone.clientHeight;
      }
      return html2canvas(clone, canvasOpts);
    }).then(function(canvas) {
      restoreStyles();
      if (clone.parentNode) document.body.removeChild(clone);
      return canvas;
    }).catch(function(err) {
      restoreStyles();
      if (clone.parentNode) document.body.removeChild(clone);
      throw err;
    });
  });
}

// =========================================================================
// injectEditableAttributes — add data-editable attrs for unified editing
// =========================================================================

function injectEditableAttributes(wrapper) {
  // Skip if already processed (saved pages already have data-editable attrs)
  if (wrapper.querySelector('[data-editable]')) return;

  // Image slots
  wrapper.querySelectorAll('.img-slot').forEach(function(slot) {
    slot.setAttribute('data-editable', 'true');
    slot.setAttribute('data-element-kind', 'image');
  });

  // Text elements
  wrapper.querySelectorAll('.editable-text').forEach(function(el) {
    el.setAttribute('data-editable', 'true');
    el.setAttribute('data-element-kind', 'text');
  });

  // Shape elements: leaf elements with visual styles, not already tagged
  var TAGS_TO_CHECK = ['DIV', 'SPAN', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'CITE'];
  var SKIP_SELECTOR = '.img-slot,.editable-text,.page-box,[data-editable],img,.tb-handle';
  wrapper.querySelectorAll('*').forEach(function(el) {
    if (el.matches(SKIP_SELECTOR)) return;
    if (TAGS_TO_CHECK.indexOf(el.tagName) === -1) return;
    if (el.children.length > 0) return; // leaf elements only
    if (el.querySelector('.img-slot,.editable-text')) return; // skip if contains editable children

    // Check for visual styles
    var cs = window.getComputedStyle(el);
    var hasVisual =
      (cs.backgroundColor && cs.backgroundColor !== 'rgba(0, 0, 0, 0)' && cs.backgroundColor !== 'transparent') ||
      (cs.borderWidth && parseFloat(cs.borderWidth) > 0) ||
      (cs.boxShadow && cs.boxShadow !== 'none') ||
      (cs.backgroundImage && cs.backgroundImage !== 'none') ||
      (el.hasAttribute('style') && /background|border|box-shadow|width:\s*\d|height:\s*\d/.test(el.getAttribute('style')));
    if (!hasVisual) return;

    el.setAttribute('data-editable', 'true');
    el.setAttribute('data-element-kind', 'shape');
  });
}

// =========================================================================
// renderPage — unified page rendering
// =========================================================================

function renderPage(options) {
  var pageId = options.pageId || '';
  var templateId = options.templateId || '';
  var albumId = options.albumId || '';
  var html = options.html || '';
  var htmlKind = options.htmlKind || 'raw-template';
  var renderClass = options.renderClass || '';
  var cssText = options.cssText || '';
  var pageSize = normalizePageSize(options.pageSize);
  var placeholders = options.placeholders || {};
  var mode = options.mode || 'editor';
  var editable = options.editable !== false;

  var uuid;
  try { uuid = self.crypto.randomUUID(); } catch(e) {
    // Fallback for non-secure contexts (HTTP without localhost/private IP)
    uuid = 'xxxx-xxxx'.replace(/x/g, function() { return (Math.random() * 16 | 0).toString(16); });
  }
  var scopedClass = 'template-scope-' + uuid;

  // 1. Process HTML
  var processedHtml = html;

  if (htmlKind === 'raw-template') {
    // Replace placeholders: {{placeholder}}, {{photo_1}}, etc.
    processedHtml = html.replace(/\{\{(\w+)\}\}/g, function(match, key) {
      return placeholders[key] !== undefined ? placeholders[key] : match;
    });
  } else {
    // saved-page: remove old scope, prevent double wrapper
    processedHtml = normalizeSavedPageHtml(html, renderClass);
  }

  // 2. Create wrapper — scopeClass and renderClass on the same element
  var wrapper = document.createElement('div');
  wrapper.className = scopedClass + (renderClass ? ' ' + renderClass : '');
  wrapper.setAttribute('data-template-render-root', '1');
  wrapper.setAttribute('data-template-id', templateId);
  wrapper.style.width = pageSize.width + 'px';
  wrapper.style.height = pageSize.height + 'px';
  wrapper.style.overflow = 'hidden';
  wrapper.style.position = 'relative';
  wrapper.innerHTML = processedHtml;

  // 3. Process and inject CSS
  var globalStyleEl = null;
  var globalCSS = '';
  var scopedCSS = '';

  if (cssText) {
    var cacheKey = albumId || '_base';
    var cacheEntry = cssCache.get(cacheKey);
    if (!cacheEntry) {
      cacheEntry = { rawCSS: cssText, globalImports: new Map() };
      cssCache.set(cacheKey, cacheEntry);
    }

    var processed = processCSS(cssText, scopedClass, renderClass);
    globalCSS = processed.globalCSS || '';
    scopedCSS = processed.scopedCSS || '';

    // Global CSS dedup by content hash
    if (globalCSS) {
      var hash = simpleHash(globalCSS);
      if (!cacheEntry.globalImports.has(hash)) {
        cacheEntry.globalImports.set(hash, { count: 0, el: null });
      }
      var gEntry = cacheEntry.globalImports.get(hash);
      gEntry.count++;
      if (!gEntry.el) {
        globalStyleEl = document.createElement('style');
        globalStyleEl.setAttribute('data-template-global', cacheKey);
        globalStyleEl.textContent = globalCSS;
        if (mode !== 'marketplace') {
          document.head.appendChild(globalStyleEl);
        }
        gEntry.el = globalStyleEl;
      } else {
        globalStyleEl = gEntry.el;
      }
    }

    // Scoped CSS goes inside the wrapper
    if (scopedCSS) {
      var localStyle = document.createElement('style');
      localStyle.setAttribute('data-template-scope', scopedClass);
      localStyle.textContent = scopedCSS;
      wrapper.insertBefore(localStyle, wrapper.firstChild);
    }
  }

  // 4. Set editability
  if (editable && mode === 'editor') {
    // Inject data-editable attributes for unified element editing
    injectEditableAttributes(wrapper);

    wrapper.querySelectorAll('.editable-text').forEach(function(el) {
      el.setAttribute('contenteditable', 'true');
    });
  } else {
    wrapper.querySelectorAll('.editable-text').forEach(function(el) {
      el.setAttribute('contenteditable', 'false');
      el.style.overflow = 'visible';
    });
  }

  // 5. Screenshot uses html2canvas with useCORS:true which handles cross-origin
  // images internally. We do NOT set crossorigin on live DOM images — that would
  // trigger CORS requests on page load, and any transient OSS CORS issue would
  // block image rendering entirely (hard to recover without user cache clearing).
  // Instead, html2canvas handles CORS at capture time on its own cloned DOM.

  // 5. For export mode (cover capture / PDF), set crossorigin on external
  // images so html2canvas can draw them without tainting the canvas.
  // We do NOT set it for editor / marketplace modes — those DOM images
  // load without CORS to avoid cached-preflight-failure blocking.
  if (mode === 'export') {
    wrapper.querySelectorAll('img[src]').forEach(function(img) {
      var src = img.getAttribute('src') || '';
      if (!/^data:/i.test(src) && !/^blob:/i.test(src) && !img.hasAttribute('crossorigin')) {
        img.setAttribute('crossorigin', 'anonymous');
      }
    });
  }

  return Promise.resolve({
    element: wrapper,
    pageSize: { width: pageSize.width, height: pageSize.height },
    scopeClass: scopedClass,
    renderClass: renderClass,
    styles: { globalCSS: globalCSS, scopedCSS: scopedCSS },
    cleanup: {
      restore: function() { /* no-op for live render */ },
      destroy: function() {
        if (wrapper.parentNode) wrapper.remove();
        // Release global style reference count
        if (globalStyleEl && cssText) {
          var cacheKey = albumId || '_base';
          var cacheEntry = cssCache.get(cacheKey);
          if (cacheEntry) {
            var hash = simpleHash(globalStyleEl.textContent);
            var gEntry = cacheEntry.globalImports.get(hash);
            if (gEntry) {
              gEntry.count--;
              if (gEntry.count <= 0) {
                if (gEntry.el && gEntry.el.parentNode) gEntry.el.remove();
                cacheEntry.globalImports.delete(hash);
              }
            }
          }
        }
      }
    },
    capture: function(captureOptions) {
      return captureElementToCanvas(wrapper, Object.assign({}, captureOptions || {}, { pageSize: pageSize }));
    }
  });
}

// =========================================================================
// buildPageRenderContext — normalize page data before rendering
// =========================================================================

function buildPageRenderContext(page, templateMeta, albumMeta, options) {
  options = options || {};

  var pageSize = normalizePageSize(
    (page && page.pageSize) ||
    (templateMeta && templateMeta.pageSize) ||
    (templateMeta && templateMeta.pageBaseSize) ||
    (albumMeta && albumMeta.pageSize) ||
    (albumMeta && albumMeta.pageBaseSize)
  );

  return {
    pageId: (page && page.id) || options.pageId || '',
    templateId: (page && (page.template || page.templateId)) || (templateMeta && templateMeta.id) || '',
    albumId: (templateMeta && templateMeta.album) || (albumMeta && albumMeta.id) || '',
    rendererVersion: 1,
    htmlKind: options.htmlKind || 'saved-page',
    html: (page && page.html) || (templateMeta && templateMeta.html_content) || '',
    pageSize: pageSize,
    renderClass: (templateMeta && templateMeta.renderClass) || (albumMeta && albumMeta.renderClass) || '',
    cssPath: (templateMeta && templateMeta.css_path) || (albumMeta && albumMeta.css) || '',
    cssHash: options.cssHash || '',
    cssText: options.cssText || '',
    placeholders: options.placeholders || {},
    imageTransforms: (page && page.imageTransforms) || {},
    textOverrides: (page && page.textOverrides) || {}
  };
}

// =========================================================================
// createExportSnapshot — frozen snapshot for export (PDF, thumbnails)
// =========================================================================

function createExportSnapshot(context) {
  return renderPage({
    pageId: context.pageId,
    templateId: context.templateId,
    albumId: context.albumId,
    html: context.html,
    htmlKind: context.htmlKind,
    renderClass: context.renderClass,
    cssText: context.cssText,
    pageSize: context.pageSize,
    placeholders: context.placeholders,
    mode: 'pdf',
    editable: false
  }).then(function(instance) {
    var snapshot = {
      pageId: context.pageId,
      templateId: context.templateId,
      rendererVersion: context.rendererVersion,
      pageSize: context.pageSize,
      renderClass: context.renderClass,
      cssText: context.cssText,
      styles: instance.styles,
      html: instance.element.outerHTML,
      element: instance.element,
      capture: instance.capture,
      destroy: instance.cleanup.destroy
    };
    return snapshot;
  });
}

// =========================================================================
// Exports
// =========================================================================

export {
  renderPage,
  captureElementToCanvas,
  processCSS,
  buildPageRenderContext,
  createExportSnapshot,
  clearCSSCache,
  normalizePageSize,
  inlineComputedVisualStyles,
  placeholderUrl
};
